import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import React from 'react';

const buttonVariants = cva(
  `inline-flex items-center justify-center rounded-xl text-sm font-semibold
   ring-offset-background transition-all duration-200 ease-in-out
   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
   disabled:pointer-events-none disabled:opacity-50
   active:scale-[0.97]`,
  {
    variants: {
      variant: {
        default:
          `bg-primary text-primary-foreground shadow-md shadow-primary/25
           hover:bg-primary/92 hover:shadow-lg hover:shadow-primary/30
           hover:-translate-y-0.5`,
        destructive:
          `bg-destructive text-destructive-foreground shadow-sm shadow-destructive/20
           hover:bg-destructive/92 hover:shadow-md hover:shadow-destructive/25`,
        outline:
          `border-2 border-primary/35 bg-transparent text-primary
           hover:bg-primary/8 hover:border-primary/60 hover:-translate-y-0.5
           shadow-none hover:shadow-sm hover:shadow-primary/10`,
        secondary:
          `bg-secondary text-secondary-foreground shadow-sm shadow-secondary/20
           hover:bg-secondary/85 hover:shadow-md hover:shadow-secondary/25`,
        ghost:
          `hover:bg-accent/60 hover:text-accent-foreground
           transition-colors duration-150`,
        link:
          `text-primary underline-offset-4 hover:underline p-0`,
        gradient:
          `bg-gradient-to-r from-brand-indigo to-brand-emerald text-white
           shadow-md shadow-brand-indigo/25 hover:shadow-lg hover:shadow-brand-indigo/30
           hover:-translate-y-0.5 hover:opacity-95`,
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm:      'h-9 rounded-lg px-4 text-xs',
        lg:      'h-12 rounded-xl px-9 text-base',
        xl:      'h-14 rounded-2xl px-12 text-base font-bold',
        icon:    'h-10 w-10',
        'icon-sm': 'h-8 w-8 rounded-lg',
        'icon-lg': 'h-12 w-12 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = 'Button';

export { Button, buttonVariants };
